import React, {
  createContext, useContext, useState, useEffect, useMemo, useCallback
} from 'react';
import {
  db, collection, doc, addDoc, updateDoc, deleteDoc,
  query, where, onSnapshot, serverTimestamp, getDocs
} from '../firebase/config';
import { useAuth } from './AuthContext';

const BookingContext = createContext();

// ============================================================
// PAYMENT POLICY HELPERS
// ============================================================

const resolvePaymentFields = (data) => {
  const optionPrice = data.optionPrice !== undefined && data.optionPrice !== null
    ? Number(data.optionPrice)
    : null;
  const basePrice = optionPrice !== null
    ? optionPrice
    : Number(data.price ?? data.servicePrice ?? 0);

  const transportFee = Number(data.transportFee || 0);
  const addonsTotal = Array.isArray(data.addons)
    ? data.addons.reduce((s, a) => s + Number(a.price || 0), 0)
    : Number(data.addonsTotal || 0);

  // Total = base + addons + transport (add-ons were missing before)
  const totalPrice = basePrice + addonsTotal + transportFee;

  const policy = data.paymentPolicy || 'after';
  const depositPercent = Number(data.depositPercent ?? 30);

  const alreadyComputed =
    data.depositAmount !== undefined &&
    data.amountDue !== undefined &&
    data.paymentStatus !== undefined;

  if (alreadyComputed) {
    return {
      price: basePrice,
      totalPrice,
      transportFee,
      addonsTotal,
      paymentPolicy: policy,
      depositPercent,
      depositAmount: Number(data.depositAmount || 0),
      amountPaid: Number(data.amountPaid || 0),
      amountDue: Number(data.amountDue || 0),
      paymentStatus: data.paymentStatus || 'unpaid',
    };
  }

  let depositAmount = 0;
  let amountDue = totalPrice;

  if (policy === 'deposit') {
    depositAmount = Math.round(totalPrice * (depositPercent / 100));
    amountDue = totalPrice;
  } else if (policy === 'full') {
    depositAmount = totalPrice;
    amountDue = totalPrice;
  }

  return {
    price: basePrice,
    totalPrice,
    transportFee,
    addonsTotal,
    paymentPolicy: policy,
    depositPercent,
    depositAmount,
    amountPaid: 0,
    amountDue,
    paymentStatus: 'unpaid',
  };
};

export const BookingProvider = ({ children }) => {
  const { user } = useAuth();

  const [pendingBookings, setPendingBookings] = useState([]);
  const [clientBookings, setClientBookings] = useState([]);
  const [clientBookingsLoading, setClientBookingsLoading] = useState(true);
  const [currentBooking, setCurrentBooking] = useState(null);
  const [loading, setLoading] = useState(false);

  // ---------- Real-time client bookings ----------
  useEffect(() => {
    if (!user?.uid) {
      setClientBookings([]);
      setClientBookingsLoading(false);
      return;
    }

    setClientBookingsLoading(true);
    const q = query(
      collection(db, 'bookings'),
      where('clientId', '==', user.uid)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        data.sort((a, b) => {
          const aT = a.createdAt?.toMillis?.() ?? new Date(a.createdAt || 0).getTime();
          const bT = b.createdAt?.toMillis?.() ?? new Date(b.createdAt || 0).getTime();
          return bT - aT;
        });
        setClientBookings(data);
        setClientBookingsLoading(false);
      },
      (error) => {
        console.error('Bookings snapshot error:', error);
        setClientBookingsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user?.uid]);

  // ---------- Persist pending queue ----------
  useEffect(() => {
    try {
      const stored = localStorage.getItem('pendingBookings');
      if (stored) setPendingBookings(JSON.parse(stored));
    } catch (e) {}
  }, []);

  useEffect(() => {
    try {
      if (pendingBookings.length > 0) {
        localStorage.setItem('pendingBookings', JSON.stringify(pendingBookings));
      } else {
        localStorage.removeItem('pendingBookings');
      }
    } catch (e) {}
  }, [pendingBookings]);

  // ---------- Pending queue ----------

  const addBooking = useCallback((bookingData) => {
    const id = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const pricing = resolvePaymentFields(bookingData);

    const newPending = {
      id,
      ...bookingData,
      ...pricing,
      addedAt: new Date().toISOString(),
    };

    setPendingBookings(prev => [newPending, ...prev]);
    return newPending;
  }, []);

  const removeBooking = useCallback((id) => {
    setPendingBookings(prev => prev.filter(b => b.id !== id));
    return true;
  }, []);

  const clearBookings = useCallback(() => {
    setPendingBookings([]);
    return true;
  }, []);

  /**
   * Strip undefined values before writing to Firestore.
   * Firestore rejects undefined — this is the fix for the
   * "Unsupported field value: undefined (found in field optionPrice)" error.
   */
  const cleanForFirestore = (obj) => {
    const out = {};
    Object.entries(obj).forEach(([k, v]) => {
      if (v !== undefined) out[k] = v;
    });
    return out;
  };

  /**
   * Compute how much should be paid NOW for a pending booking.
   * 'full'    → totalPrice
   * 'deposit' → depositAmount
   * 'after'   → 0
   */
  const computePayNowForBooking = (b) => {
    const total = Number(b.totalPrice ?? (b.price || 0) + Number(b.addonsTotal || 0) + (b.transportFee || 0));
    const policy = b.paymentPolicy || 'after';
    if (policy === 'full') return total;
    if (policy === 'deposit') return Number(b.depositAmount || Math.round(total * ((b.depositPercent || 30) / 100)));
    return 0;
  };

  /**
   * Total "pay now" across all pending bookings.
   * Used by CartDrawer to know whether to show a pay button.
   */
  const computePendingPayNow = useCallback(() => {
    return pendingBookings.reduce((sum, b) => sum + computePayNowForBooking(b), 0);
  }, [pendingBookings]);

  /**
   * Batch-write all pending bookings to Firestore (unpaid).
   * Used when ALL bookings are pay-after (no payment needed now).
   */
  const confirmBookings = useCallback(async () => {
    if (!user?.uid) {
      throw new Error('Must be signed in to confirm bookings');
    }
    if (pendingBookings.length === 0) return true;

    setLoading(true);
    try {
      const writes = pendingBookings.map(pending => {
        const {
          id: _pendingId,
          addedAt: _addedAt,
          ...payload
        } = pending;

        const pricing = resolvePaymentFields(payload);
        const clean = cleanForFirestore({
          ...payload,
          ...pricing,
          clientId: user.uid,
          status: 'pending',
          createdAt: serverTimestamp(),
        });

        return addDoc(collection(db, 'bookings'), clean);
      });

      await Promise.all(writes);
      setPendingBookings([]);
      return true;
    } catch (error) {
      console.error('confirmBookings failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [pendingBookings, user?.uid]);

  /**
   * NEW — Batch-write all pending bookings AND stamp payment info.
   *
   * Called from CartDrawer after Paystack success.
   *
   * @param {object} opts
   * @param {string} opts.paymentReference  Paystack reference
   * @param {string} opts.paymentMethod     'paystack' (default)
   *
   * Per booking:
   *   - 'full'    → paymentStatus: 'paid_in_full', amountPaid: totalPrice
   *   - 'deposit' → paymentStatus: 'deposit_paid', amountPaid: depositAmount, amountDue: balance
   *   - 'after'   → paymentStatus: 'unpaid'   (nothing due now)
   */
  const confirmBookingsAndPay = useCallback(async ({
    paymentReference,
    paymentMethod = 'paystack',
  } = {}) => {
    if (!user?.uid) {
      throw new Error('Must be signed in to confirm bookings');
    }
    if (pendingBookings.length === 0) return true;

    setLoading(true);
    try {
      const paidAt = serverTimestamp();
      const writes = pendingBookings.map(pending => {
        const {
          id: _pendingId,
          addedAt: _addedAt,
          ...payload
        } = pending;

        const pricing = resolvePaymentFields(payload);
        const policy = pricing.paymentPolicy;

        let paymentStatus = 'unpaid';
        let amountPaid = 0;
        let amountDue = pricing.totalPrice;

        if (policy === 'full') {
          paymentStatus = 'paid_in_full';
          amountPaid = pricing.totalPrice;
          amountDue = 0;
        } else if (policy === 'deposit') {
          paymentStatus = 'deposit_paid';
          amountPaid = pricing.depositAmount;
          amountDue = pricing.totalPrice - pricing.depositAmount;
        }

        const clean = cleanForFirestore({
          ...payload,
          ...pricing,
          clientId: user.uid,
          status: 'pending',
          paymentStatus,
          amountPaid,
          amountDue,
          paymentMethod,
          paymentReference,
          paidAt,
          createdAt: serverTimestamp(),
        });

        return addDoc(collection(db, 'bookings'), clean);
      });

      await Promise.all(writes);
      setPendingBookings([]);
      return true;
    } catch (error) {
      console.error('confirmBookingsAndPay failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [pendingBookings, user?.uid]);

  // ---------- Legacy ----------

  const createBooking = useCallback(async (bookingData) => {
    if (!user?.uid) throw new Error('Must be signed in to book');
    setLoading(true);
    try {
      const pricing = resolvePaymentFields(bookingData);
      const clean = cleanForFirestore({
        ...bookingData,
        ...pricing,
        clientId: user.uid,
        status: 'pending',
        createdAt: serverTimestamp(),
      });
      const ref = await addDoc(collection(db, 'bookings'), clean);
      const newBooking = { id: ref.id, ...clean, clientId: user.uid, status: 'pending' };
      setCurrentBooking(newBooking);
      return newBooking;
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  const updateBooking = useCallback(async (id, updates) => {
    try {
      await updateDoc(doc(db, 'bookings', id), {
        ...updates,
        updatedAt: serverTimestamp(),
      });
      if (currentBooking?.id === id) {
        setCurrentBooking(prev => ({ ...prev, ...updates }));
      }
      return true;
    } catch (error) {
      console.error('updateBooking failed:', error);
      return false;
    }
  }, [currentBooking]);

  const cancelBooking = useCallback(async (id) => {
    return updateBooking(id, { status: 'cancelled' });
  }, [updateBooking]);

  const getBookingsByStatus = useCallback((status) => {
    return clientBookings.filter(b => b.status === status);
  }, [clientBookings]);

  const value = useMemo(() => ({
    pendingBookings,
    addBooking,
    removeBooking,
    clearBookings,
    confirmBookings,
    confirmBookingsAndPay,           // ← NEW
    computePendingPayNow,             // ← NEW

    clientBookings,
    clientBookingsLoading,

    bookings: clientBookings,
    currentBooking,
    loading,
    createBooking,
    updateBooking,
    cancelBooking,
    getBookingsByStatus,
  }), [
    pendingBookings, addBooking, removeBooking, clearBookings,
    confirmBookings, confirmBookingsAndPay, computePendingPayNow,
    clientBookings, clientBookingsLoading, currentBooking, loading,
    createBooking, updateBooking, cancelBooking, getBookingsByStatus,
  ]);

  return (
    <BookingContext.Provider value={value}>
      {children}
    </BookingContext.Provider>
  );
};

export const useBooking = () => {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error('useBooking must be used within a BookingProvider');
  }
  return context;
};